/**
 * AppToaster Component
 *
 * Single global toast notification host for the entire app.
 *
 * Domains and shared code emit toasts via `toast.success/error/...` from
 * `sonner` (or `@parrot-software/design-system`). They MUST NOT mount their
 * own `<Toaster>` — `sonner` is a singleton emitter, so multiple Toasters
 * cause every toast to render once per mount.
 *
 * @module components/AppToaster
 */

import type { CSSProperties } from 'react';
import { Toaster } from '@parrot-software/design-system';
import { CircleX, CircleCheckBig } from '@parrot-software/design-system/icons';

const SIDEBAR_HALF_WIDTH_EXPANDED = 130;
const SIDEBAR_HALF_WIDTH_COLLAPSED = 32;

const toasterStyles = {
  toast: 'group toast group-[.toaster]:shadow-lg border-3 w-fit!',
  description: 'group-[.toast]:text-muted-foreground',
  actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
  cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
  success: 'border-success! border!',
  error: 'border-destructive! border!',
};

export interface AppToasterProps {
  sidebarExpanded?: boolean;
}

/**
 * AppToaster Component
 *
 * Pre-configured global Toaster with:
 * - Light theme
 * - Custom icons for success/error
 * - Consistent border styling
 * - Auto-fit width
 * - Sidebar-aware horizontal centering on desktop
 *
 * @param sidebarExpanded - Current sidebar state. When true, the toast is shifted
 *   right by half the expanded sidebar width so it visually centers in the
 *   content area instead of the full viewport. Below the `sm` breakpoint the
 *   sidebar overlays the content, so no offset is applied.
 *
 * @returns {React.ReactElement} Configured Toaster component
 */
export const AppToaster = ({ sidebarExpanded = true }: AppToasterProps): React.ReactElement => {
  const sidebarOffset = sidebarExpanded ? SIDEBAR_HALF_WIDTH_EXPANDED : SIDEBAR_HALF_WIDTH_COLLAPSED;
  const toasterStyle = { '--toaster-sidebar-offset': `${sidebarOffset}px` } as CSSProperties;

  return (
    <Toaster
      className="sm:left-[calc(50%+var(--toaster-sidebar-offset))]!"
      icons={{
        error: <CircleX className="size-4" strokeWidth={2.5} />,
        success: <CircleCheckBig className="size-4" strokeWidth={2.5} />,
      }}
      position="top-center"
      richColors
      style={toasterStyle}
      theme="light"
      toastOptions={{
        classNames: toasterStyles,
      }}
    />
  );
};
